/**
 * Sincronização do link compartilhável com todos os codecs registrados
 * (`getUrlCodecs()`): lê a URL uma vez na inicialização e depois reescreve a
 * query string (`history.replaceState`, com debounce) quando o estado muda.
 *
 * PREFERÊNCIAS SALVAS (23/09/2026, a pedido do usuário: "sempre manter salvo
 * as modificações feitas pelo usuário"): no mesmo debounce, a mesma query —
 * montada com `persist` de cada codec, que tira o instante do quadro — vai
 * para o localStorage. Ao abrir SEM parâmetros nossos na URL, ela é lida no
 * lugar do link. Regras:
 * - um link com qualquer parâmetro nosso manda INTEIRO (as preferências salvas
 *   não se misturam: o link omite o que está no padrão, e misturar mostraria
 *   camadas que quem compartilhou não tinha);
 * - aberto por link, só grava quando o usuário muda algo (o estado do link não
 *   substitui as preferências de quem só foi olhar);
 * - o instante nunca volta: ao abrir, é sempre a medição mais recente;
 * - sem localStorage (navegação privada, bloqueado), tudo funciona, só não lembra.
 */
import { getUrlCodecs } from './urlCodec';

/** Ordem preferida dos parâmetros (as demais chaves vêm depois, em ordem alfabética). */
const KEY_ORDER = ['c', 'z', 'g', 't', 'q', 'v', 'f', 'a', 'm'];
const DEBOUNCE_MS = 300;

/** Codifica só o necessário: mantém `, : ; / @ ! * ( ) ~ -` legíveis. */
export function encodeQueryValue(value: string): string {
  return encodeURIComponent(value).replace(/%(2C|3A|3B|2F|40|21|2A|28|29|7E)/gi, (_, hex: string) =>
    String.fromCharCode(parseInt(hex, 16)),
  );
}

function sortKeys(a: string, b: string): number {
  const ia = KEY_ORDER.indexOf(a);
  const ib = KEY_ORDER.indexOf(b);
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return a.localeCompare(b);
}

/** Valores fixados por chave (o do link e o das preferências), ver `congelarCodecs`. */
let congelados: Map<string, { link: string | null; salvo: string | null }> | null = null;

/**
 * Fixa no link e nas preferências o valor ATUAL de todos os codecs, menos os
 * de `manter`, até a função devolvida ser chamada. O "Próximos dias" desliga
 * as camadas observadas ao entrar e as religa ao sair: enquanto isso, o link
 * e as preferências continuam com o observado de antes (recarregar no modo,
 * ou fechar a aba, não perde as camadas da pessoa).
 */
export function congelarCodecs(manter: ReadonlySet<string>): () => void {
  const fixos = new Map<string, { link: string | null; salvo: string | null }>();
  for (const codec of getUrlCodecs()) {
    if (manter.has(codec.key)) continue;
    try {
      const link = codec.encode();
      fixos.set(codec.key, { link, salvo: codec.persist ? codec.persist() : link });
    } catch (e) {
      console.warn(`[AmaView] Falha ao codificar "${codec.key}":`, e);
    }
  }
  congelados = fixos;
  return () => {
    if (congelados === fixos) congelados = null;
  };
}

/** Aplica os valores da URL a cada codec (tolerante a erros). */
export function readUrlState(search: string = window.location.search): void {
  const params = new URLSearchParams(search);
  for (const codec of getUrlCodecs()) {
    const value = params.get(codec.key);
    if (value === null) continue;
    try {
      codec.decode(value);
    } catch (e) {
      console.warn(`[AmaView] Parâmetro "${codec.key}" inválido no link:`, e);
    }
  }
}

/**
 * Query string ("?c=…&z=…", ou "") com o estado atual de todos os codecs.
 * `forStorage`: a versão das preferências salvas (`persist` de cada codec,
 * sem o instante do quadro e sem parâmetros alheios).
 */
export function buildQuery(search: string = window.location.search, opts: { forStorage?: boolean } = {}): string {
  const codecs = getUrlCodecs();
  const managed = new Set(codecs.map((c) => c.key));
  const pairs: [string, string][] = [];
  for (const codec of codecs) {
    const congelado = congelados?.get(codec.key);
    let value: string | null = null;
    try {
      if (congelado) value = opts.forStorage ? congelado.salvo : congelado.link;
      else value = opts.forStorage && codec.persist ? codec.persist() : codec.encode();
    } catch (e) {
      console.warn(`[AmaView] Falha ao codificar "${codec.key}":`, e);
    }
    if (value !== null && value !== '') pairs.push([codec.key, value]);
  }
  // Mantém parâmetros que não são nossos (ex.: utm_*) — no link, não nas preferências.
  if (!opts.forStorage) {
    for (const [k, v] of new URLSearchParams(search)) {
      if (!managed.has(k)) pairs.push([k, v]);
    }
  }
  pairs.sort((a, b) => sortKeys(a[0], b[0]));
  const query = pairs.map(([k, v]) => `${encodeQueryValue(k)}=${encodeQueryValue(v)}`).join('&');
  return query ? `?${query}` : '';
}

/** Link completo do estado atual (para o botão Compartilhar). */
export function currentShareUrl(): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}${buildQuery()}`;
}

/** Escreve a URL agora (sem esperar o debounce). */
export function writeUrlNow(): void {
  const { pathname, hash, search } = window.location;
  const query = buildQuery(search);
  if (query !== search) window.history.replaceState(window.history.state, '', `${pathname}${query}${hash}`);
}

// ---------------------------------------------------------------------------
// Preferências salvas
// ---------------------------------------------------------------------------

export const PREFS_KEY = 'amaview:preferencias';
/**
 * O player estava tocando quando a página foi deixada ("1") — recarregar
 * retoma a animação (loop/playback.ts e startup.ts). Fora da query: o link
 * compartilhado não carrega isso, é hábito de quem está vendo.
 */
export const PLAYING_KEY = 'amaview:tocando';

/** A página abriu com um link que traz parâmetros nossos (ver `startupSearch`). */
let openedFromLink = false;
/** "Restaurar padrões" em andamento: nada mais é gravado até a página recarregar. */
let prefsFrozen = false;

/** A query tem algum parâmetro de um codec registrado? */
export function hasManagedParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return getUrlCodecs().some((c) => params.has(c.key));
}

/** Preferências salvas (query "?…", "" se salvas no padrão) ou null se não há. */
export function readSavedQuery(): string | null {
  try {
    const v = globalThis.localStorage?.getItem(PREFS_KEY);
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

function writeSavedQuery(query: string): void {
  if (prefsFrozen) return;
  try {
    globalThis.localStorage?.setItem(PREFS_KEY, query);
  } catch {
    // navegação privada / armazenamento cheio ou bloqueado: só não lembra
  }
}

/**
 * Query a aplicar na inicialização: a da URL, se ela traz algum parâmetro
 * nosso (um link compartilhado manda); senão, as preferências salvas.
 */
export function startupSearch(search: string = window.location.search): string {
  openedFromLink = hasManagedParams(search);
  if (openedFromLink) return search;
  return readSavedQuery() ?? '';
}

/**
 * Volta tudo ao padrão: apaga as preferências salvas e recarrega a página sem
 * parâmetros (cada módulo nasce no seu padrão — mais seguro que desfazer
 * estado por estado). Sem confirmação: o usuário pediu.
 */
export function resetPreferences(): void {
  prefsFrozen = true;
  try {
    globalThis.localStorage?.removeItem(PREFS_KEY);
    globalThis.localStorage?.removeItem(PLAYING_KEY);
  } catch {
    // sem armazenamento: não havia o que apagar
  }
  window.location.replace(`${window.location.pathname}${window.location.hash}`);
}

/** Passa a reescrever a URL quando o estado muda. Retorna o cancelamento. */
export function startUrlSync(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  // Aberto por link: a primeira gravação é a referência; só grava as
  // preferências quando o estado se afasta dela (o usuário mexeu em algo).
  let baseline: string | null = null;
  let userChanged = !openedFromLink;
  const saveNow = () => {
    const query = buildQuery('', { forStorage: true });
    if (!userChanged) {
      if (baseline === null) baseline = query;
      if (query === baseline) return;
      userChanged = true;
    }
    if (readSavedQuery() !== query) writeSavedQuery(query);
  };
  const onChange = () => {
    // Uma gravação agendada por vez, SEM reiniciar o relógio: tocando, o quadro
    // muda a cada 200 ms (< DEBOUNCE_MS) e o debounce puro nunca chegava ao fim
    // — o link não era atualizado enquanto a animação rodava. A gravação lê o
    // estado da hora, então nada se perde.
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      writeUrlNow();
      saveNow();
    }, DEBOUNCE_MS);
  };
  // Fechar a aba logo depois de mexer (dentro do debounce) não perde a mudança.
  const flush = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
    writeUrlNow();
    saveNow();
  };
  window.addEventListener('pagehide', flush);
  const unsubs = getUrlCodecs().map((codec) => codec.subscribe(onChange));
  onChange();
  return () => {
    window.removeEventListener('pagehide', flush);
    unsubs.forEach((u) => u());
    if (timer) clearTimeout(timer);
  };
}
